import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router'
import MakeHeader from '../ReusableComponents/Header/MakeHeader'
import getBackendImage from '../../source/imageUtil'
import renderImage from '../../source/importImg'

const BACKEND_URL = process.env.REACT_APP_API_BASE

const ExpansionKitAdmin = () => {
    const history = useHistory()
    const [kits, setKits] = useState([])
    const [loading, setLoading] = useState(true)
    const [uploadingId, setUploadingId] = useState(null)

    // Fetch the expansion-kit catalog from the dedicated live table
    const fetchKits = () => {
        fetch(`${BACKEND_URL}/api/admin/expansion-kits`, {
            credentials: 'include',
        })
            .then((res) => res.json())
            .then((data) => {
                setKits(data)
                setLoading(false)
            })
            .catch((err) => {
                console.error('Failed to load expansion kits:', err)
                setLoading(false)
            })
    }

    useEffect(() => {
        fetchKits()
    }, [])

    const handleTitleChange = (id, newTitle) => {
        setKits(kits.map((k) => (k.id === id ? { ...k, title: newTitle } : k)))
    }

    const handleImageUpload = async (e, id) => {
        const file = e.target.files && e.target.files[0]
        if (!file) return

        if (file.type !== 'image/png') {
            alert('Only .png images are allowed.')
            return
        }

        const formData = new FormData()
        formData.append('image', file)

        setUploadingId(id)
        try {
            const res = await fetch(`${BACKEND_URL}/api/upload-image`, {
                method: 'POST',
                body: formData,
                credentials: 'include',
            })
            if (!res.ok) throw new Error('Image upload failed')
            const data = await res.json()

            // Update local state collection with the returned filename string key
            setKits(
                kits.map((k) =>
                    k.id === id ? { ...k, image: data.filename } : k
                )
            )
        } catch (err) {
            console.error('Error uploading kit asset:', err)
            alert('Failed to upload image pointer asset.')
        }
        setUploadingId(null)
    }

    const handleSaveChanges = async () => {
        try {
            const updateRes = await fetch(
                `${BACKEND_URL}/api/admin/expansion-kits`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ subprograms: kits }),
                    credentials: 'include',
                }
            )

            if (updateRes.ok) {
                alert('Expansion kits saved and published live!')
            } else {
                throw new Error('Failed to update records')
            }
        } catch (err) {
            console.error('Save routine error:', err)
            alert('Failed to update expansion kit configurations.')
        }
    }

    if (loading) {
        return (
            <div
                style={{ padding: '40px', textAlign: 'center', color: '#888' }}
            >
                Loading Expansion Kit Staging System...
            </div>
        )
    }

    return (
        <div
            style={{
                padding: '20px',
                background: '#f8f9fa',
                minHeight: '100vh',
            }}
        >
            <MakeHeader
                title="Admin: Manage Expansion Kits"
                isGoBack={true}
                goBack={() => history.push('/admin')}
            />

            <div
                style={{
                    maxWidth: '1000px',
                    margin: '30px auto',
                    background: '#fff',
                    borderRadius: '12px',
                    padding: '24px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '12px',
                        marginBottom: '20px',
                    }}
                >
                    <button
                        onClick={handleSaveChanges}
                        style={{
                            padding: '10px 20px',
                            background: '#28a745',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            fontWeight: '600',
                            cursor: 'pointer',
                        }}
                    >
                        Save Changes
                    </button>
                </div>

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        // Adds vertical constraint and scrollbar behavior
                        maxHeight: '70vh', 
                        overflowY: 'auto',
                        paddingRight: '8px' // Keeps scrollbar from overlapping text boxes
                    }}
                >
                    {kits.map((kit) => {
                        const hasCustomImage =
                            kit.image &&
                            kit.image !== 'projectDefault' &&
                            !kit.image.includes('Expansion')
                        const displayImageSrc = kit.image
                            ? getBackendImage(kit.image)
                            : ''

                        return (
                            <div
                                key={kit.id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '20px',
                                    padding: '16px',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    background: '#fafafa',
                                }}
                            >
                                <div
                                    style={{
                                        width: '80px',
                                        height: '80px',
                                        borderRadius: '6px',
                                        border: '1px dashed #cbd5e1',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                        background: '#fff',
                                    }}
                                >
                                    {kit.image ? (
                                        <img
                                            src={displayImageSrc}
                                            alt={kit.title}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain',
                                            }}
                                        />
                                    ) : (
                                        <span
                                            style={{
                                                fontSize: '0.75rem',
                                                color: '#94a3b8',
                                                textAlign: 'center',
                                                padding: '4px',
                                            }}
                                        >
                                            No Asset Found
                                        </span>
                                    )}
                                </div>

                                <div
                                    style={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '8px',
                                    }}
                                >
                                    <label
                                        style={{
                                            fontSize: '0.85rem',
                                            fontWeight: '600',
                                            color: '#475569',
                                        }}
                                    >
                                        Kit Title Display Name
                                    </label>
                                    <input
                                        type="text"
                                        value={kit.title}
                                        onChange={(e) =>
                                            handleTitleChange(
                                                kit.id,
                                                e.target.value
                                            )
                                        }
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid #cbd5e1',
                                            borderRadius: '6px',
                                            fontSize: '0.95rem',
                                        }}
                                    />
                                </div>

                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '6px',
                                    }}
                                >
                                    <input
                                        type="file"
                                        accept="image/png"
                                        id={`file-${kit.id}`}
                                        style={{ display: 'none' }}
                                        onChange={(e) =>
                                            handleImageUpload(e, kit.id)
                                        }
                                    />
                                    <label
                                        htmlFor={`file-${kit.id}`}
                                        style={{
                                            padding: '8px 14px',
                                            background: '#475569',
                                            color: '#fff',
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            fontWeight: '500',
                                            cursor:
                                                uploadingId === kit.id
                                                    ? 'wait'
                                                    : 'pointer',
                                            textAlign: 'center',
                                        }}
                                    >
                                        {uploadingId === kit.id
                                            ? 'Uploading...'
                                            : 'Change Image (.png)'}
                                    </label>
                                    <span
                                        style={{
                                            fontSize: '0.75rem',
                                            color: '#64748b',
                                            textAlign: 'center',
                                        }}
                                    >
                                        System ID: {kit.id}
                                    </span>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

export default ExpansionKitAdmin
